/**
 * Shared stat-value formatters used by the global stats bar (`StatItem`) and the
 * map selection stats cards (`MapSelectionStatsCard`). Keep both consumers on these
 * functions so the displayed values never diverge between the two surfaces.
 */

export type StatFormat = 'integer' | 'volume';

/** Placeholder shown when a stat value is missing (null/undefined). */
export const STAT_PLACEHOLDER = '—';

/**
 * Formats a count with thousands separators (en-GB), collapsing values of a
 * million or more to a single-decimal "M" suffix (e.g. 1_250_000 -> "1.3M").
 */
export function formatInteger(value: number): string {
    if (value >= 1_000_000) {
        return `${(value / 1_000_000).toFixed(1)}M`;
    }
    return new Intl.NumberFormat('en-GB').format(value);
}

/**
 * Formats a raw volume value into Ml/D (the value is stored in a base unit and
 * divided down to megalitres), fixed to two decimal places. The "Ml/D" unit is
 * carried in the stat label, not the value.
 */
export function formatVolume(value: number): string {
    return (value / 1_000_000).toFixed(2);
}

/** Formats a stat value, returning {@link STAT_PLACEHOLDER} for null/undefined. */
export function formatStatValue(value: number | null | undefined, format: StatFormat): string {
    if (value == null) {
        return STAT_PLACEHOLDER;
    }
    return format === 'volume' ? formatVolume(value) : formatInteger(value);
}
